/**
 * PackageFormPage — Classcard-parity package form (11E.4 §C).
 *
 * Single scrollable page with a sticky section nav, replacing the old
 * PackageFormModal (kept only for the legacy /admin/catalog surface).
 * Routes: /packages/new, /packages/:id/edit.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Input } from '@/components/common/Input';
import { Badge } from '@/components/common/Badge';
import { PageContainer } from '@/components/layout';
import { HelpTip } from '@/components/entity';
import { ImageUpload } from '@/components/entity/ImageUpload';
import { ApiError } from '@core/api/client';
import { useAuth } from '@/context';
import {
  usePackage,
  useCreatePackage,
  useUpdatePackage,
  type PackageCreate,
  type PackageAddOn,
  type PackageType,
  type BillingCycle,
  type ValidityType,
} from '@features/catalog/api/packages';
import { useSubjects } from '@features/catalog/api/subjects';
import { useCatalogConfig } from '@features/catalog/api/config';
import { paiseToRupeeInput, rupeeInputToPaise, formatPaise } from '@features/catalog/config/ui';
import {
  ALL_GRADES,
  deriveCycleLabel,
  deriveName,
  getAudienceOptions,
} from '@features/packages/config/nameDerivation';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StickyNav = styled.nav`
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  @media (max-width: 900px) {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const NavLink = styled.a`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  color: #475569;
  text-decoration: none;
  &:hover { background: #f1f5f9; }
`;

const Section = styled.section`
  scroll-margin-top: 16px;
  margin-bottom: 28px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, 1fr);
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ToggleRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
  margin-top: 4px;
`;

const NamePreview = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #4338ca;
  padding: 10px 14px;
  background: #eef2ff;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const SubjectChip = styled.label<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${({ $active }) => ($active ? '#e0e7ff' : '#f8fafc')};
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
`;

const AddOnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;

const SECTIONS = [
  { id: 'basics', label: 'Basics' },
  { id: 'type', label: 'Type' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'price', label: 'Price' },
  { id: 'validity', label: 'Validity' },
  { id: 'enrollment', label: 'Enrollment limit' },
  { id: 'includes', label: 'Includes' },
];

const EMPTY: PackageCreate = {
  name: '',
  subject_ids: [],
  sessions_count: 4,
  sessions_unlimited: false,
  validity_days: 30,
  validity_type: 'by_range',
  validity_end_date: null,
  price: 0,
  tax_inclusive: true,
  status: 'active',
  package_type: 'one_time',
  billing_cycle: null,
  audience_label: ALL_GRADES,
  name_override: null,
  display_image_url: null,
  makeup_sessions_allowed: false,
  enrollment_limit_days_per_week: null,
  pro_rata_enabled: false,
  published_online: false,
  registration_fee: 0,
  add_ons: [],
  reschedule_limit: 2,
};

export const PackageFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;

  const { data: existing, isLoading: isLoadingExisting } = usePackage(id ?? '');
  const { data: subjectsData } = useSubjects({ status: 'active' });
  const { rules } = useCatalogConfig();
  const createMut = useCreatePackage();
  const updateMut = useUpdatePackage();

  const [form, setForm] = useState<PackageCreate>(EMPTY);
  const [priceInput, setPriceInput] = useState('0');
  const [regFeeInput, setRegFeeInput] = useState('0');
  const [validityEndDate, setValidityEndDate] = useState('');

  useEffect(() => {
    if (isEdit && existing) {
      const rest: PackageCreate = {
        name: existing.name,
        subject_ids: existing.subject_ids,
        sessions_count: existing.sessions_count,
        sessions_unlimited: existing.sessions_unlimited,
        validity_days: existing.validity_days,
        validity_type: existing.validity_type,
        validity_end_date: existing.validity_end_date,
        price: existing.price,
        tax_inclusive: existing.tax_inclusive,
        status: existing.status,
        package_type: existing.package_type,
        billing_cycle: existing.billing_cycle,
        audience_label: existing.audience_label,
        name_override: existing.name_override,
        display_image_url: existing.display_image_url,
        makeup_sessions_allowed: existing.makeup_sessions_allowed,
        enrollment_limit_days_per_week: existing.enrollment_limit_days_per_week,
        pro_rata_enabled: existing.pro_rata_enabled,
        published_online: existing.published_online,
        registration_fee: existing.registration_fee,
        add_ons: existing.add_ons,
        reschedule_limit: existing.reschedule_limit,
      };
      setForm(rest);
      setPriceInput(paiseToRupeeInput(existing.price));
      setRegFeeInput(paiseToRupeeInput(existing.registration_fee));
      setValidityEndDate(existing.validity_end_date ?? '');
    }
  }, [isEdit, existing]);

  const subjects = useMemo(() => subjectsData?.items ?? [], [subjectsData]);
  const subjectNameById = useMemo(
    () => new Map(subjects.map((s) => [s.id, s.name])),
    [subjects],
  );
  const selectedSubjectNames = form.subject_ids.map((sid) => subjectNameById.get(sid) ?? sid);
  const selectedSubjectCategories = subjects
    .filter((s) => form.subject_ids.includes(s.id))
    .map((s) => s.category);
  const audienceOptions = getAudienceOptions(selectedSubjectCategories);

  const cycleLabel = deriveCycleLabel(form);
  const derivedName = deriveName(selectedSubjectNames, form.audience_label, cycleLabel);
  const previewName = form.name_override || derivedName;

  const update = <K extends keyof PackageCreate>(key: K, value: PackageCreate[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleSubject = (subjId: string) => {
    setForm((f) => ({
      ...f,
      subject_ids: f.subject_ids.includes(subjId)
        ? f.subject_ids.filter((x) => x !== subjId)
        : [...f.subject_ids, subjId],
    }));
  };

  const addOnRows: PackageAddOn[] = form.add_ons;
  const setAddOn = (idx: number, patch: Partial<PackageAddOn>) => {
    setForm((f) => ({
      ...f,
      add_ons: f.add_ons.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
    }));
  };
  const addAddOnRow = () => update('add_ons', [...form.add_ons, { name: '', price: 0 }]);
  const removeAddOnRow = (idx: number) =>
    update('add_ons', form.add_ons.filter((_, i) => i !== idx));

  const clampNumber = (n: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo));

  const handleSubmit = async () => {
    if (form.subject_ids.length === 0) {
      toast.error('Pick at least one subject');
      return;
    }
    if (form.package_type === 'subscription' && !form.billing_cycle) {
      toast.error('Pick a billing cycle for subscription packages');
      return;
    }
    const payload: PackageCreate = {
      ...form,
      name: derivedName,
      sessions_count: clampNumber(
        form.sessions_count,
        rules.package_sessions_min,
        rules.package_sessions_max,
      ),
      validity_days: clampNumber(
        form.validity_days,
        rules.package_validity_days_min,
        rules.package_validity_days_max,
      ),
      validity_end_date: form.validity_type === 'by_date' ? validityEndDate || null : null,
      price: Math.max(0, rupeeInputToPaise(priceInput)),
      registration_fee: Math.max(0, rupeeInputToPaise(regFeeInput)),
      billing_cycle: form.package_type === 'subscription' ? form.billing_cycle : null,
      pro_rata_enabled: form.package_type === 'subscription' ? form.pro_rata_enabled : false,
      add_ons: form.add_ons.filter((a) => a.name.trim().length > 0),
    };
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync({ id, body: payload });
        toast.success('Package updated');
      } else {
        const created = await createMut.mutateAsync(payload);
        toast.success('Package created');
        navigate(`/packages/${created.id}`);
        return;
      }
      navigate(`/packages/${id}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to save package';
      toast.error(msg);
    }
  };

  if (isEdit && isLoadingExisting) {
    return (
      <PageContainer title="Loading">
        <Card padding="lg"><p style={{ color: '#64748b' }}>Loading package…</p></Card>
      </PageContainer>
    );
  }

  const loading = createMut.isPending || updateMut.isPending;

  return (
    <PageContainer
      title={isEdit ? 'Edit Package' : 'New Package'}
      data-testid="candeza-package-form-page"
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/packages')} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading} data-testid="candeza-package-form-submit-btn">
            {isEdit ? 'Save changes' : 'Create package'}
          </Button>
        </>
      }
    >
      <Layout>
        <StickyNav data-testid="candeza-package-form-nav">
          {SECTIONS.map((s) => (
            <NavLink key={s.id} href={`#${s.id}`}>{s.label}</NavLink>
          ))}
        </StickyNav>

        <Card padding="lg">
          <NamePreview data-testid="candeza-package-name-preview">{previewName}</NamePreview>

          <Section id="basics">
            <SectionTitle>Basics <HelpTip tipKey="packages.name_preview" /></SectionTitle>
            <Grid>
              <Input
                label="Name override (optional)"
                value={form.name_override ?? ''}
                onChange={(e) => update('name_override', e.target.value || null)}
                placeholder="Leave blank to use the auto-derived name"
                data-testid="candeza-package-name-override-input"
              />
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Audience <HelpTip tipKey="packages.audience" />
                </label>
                <select
                  value={form.audience_label}
                  onChange={(e) => update('audience_label', e.target.value)}
                  data-testid="candeza-package-audience-select"
                  style={{ width: '100%', height: 44, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 12px' }}
                >
                  {audienceOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </Grid>
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Display image</div>
              {id && tenantId ? (
                <ImageUpload
                  currentUrl={form.display_image_url}
                  onUpload={(url) => update('display_image_url', url)}
                  storagePath={id}
                  tenantId={tenantId}
                  entityType="packages"
                />
              ) : (
                <p style={{ fontSize: 13, color: '#94a3b8' }}>Save the package first to add an image.</p>
              )}
            </div>
          </Section>

          <Section id="type">
            <SectionTitle>Type <HelpTip tipKey="packages.type" /></SectionTitle>
            <Grid>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Package type</label>
                <select
                  value={form.package_type}
                  onChange={(e) => update('package_type', e.target.value as PackageType)}
                  data-testid="candeza-package-type-select"
                  style={{ width: '100%', height: 44, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 12px' }}
                >
                  <option value="one_time">One-time</option>
                  <option value="subscription">Subscription</option>
                </select>
              </div>
              {form.package_type === 'subscription' && (
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    Billing cycle <HelpTip tipKey="packages.billing_cycle" />
                  </label>
                  <select
                    value={form.billing_cycle ?? ''}
                    onChange={(e) => update('billing_cycle', e.target.value as BillingCycle)}
                    data-testid="candeza-package-billing-cycle-select"
                    style={{ width: '100%', height: 44, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 12px' }}
                  >
                    <option value="">Select…</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="half_yearly">Half-yearly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
              )}
            </Grid>
            {form.package_type === 'subscription' && (
              <ToggleRow>
                <input
                  type="checkbox"
                  checked={form.pro_rata_enabled}
                  onChange={(e) => update('pro_rata_enabled', e.target.checked)}
                  data-testid="candeza-package-pro-rata-toggle"
                />
                Pro-rata price adjustment on mid-cycle start
                <HelpTip tipKey="packages.pro_rata" />
              </ToggleRow>
            )}
            <ToggleRow>
              <input
                type="checkbox"
                checked={form.published_online}
                onChange={(e) => update('published_online', e.target.checked)}
                data-testid="candeza-package-published-online-toggle"
              />
              Publish on the public booking page
              <HelpTip tipKey="packages.published_online" />
            </ToggleRow>
          </Section>

          <Section id="sessions">
            <SectionTitle>Sessions <HelpTip tipKey="packages.sessions" /></SectionTitle>
            <Grid>
              <Input
                label="Sessions"
                type="number"
                min={rules.package_sessions_min}
                max={rules.package_sessions_max}
                value={String(form.sessions_count)}
                disabled={form.sessions_unlimited}
                onChange={(e) => update('sessions_count', Number(e.target.value))}
                data-testid="candeza-package-sessions-input"
              />
            </Grid>
            <ToggleRow>
              <input
                type="checkbox"
                checked={form.sessions_unlimited}
                onChange={(e) => update('sessions_unlimited', e.target.checked)}
                data-testid="candeza-package-sessions-unlimited-toggle"
              />
              Unlimited sessions
            </ToggleRow>
            <ToggleRow>
              <input
                type="checkbox"
                checked={form.makeup_sessions_allowed}
                onChange={(e) => update('makeup_sessions_allowed', e.target.checked)}
                data-testid="candeza-package-makeup-toggle"
              />
              Allow make-up sessions
            </ToggleRow>
            <Grid>
              <Input
                label="Reschedule limit"
                type="number"
                min={0}
                max={20}
                value={String(form.reschedule_limit)}
                onChange={(e) => update('reschedule_limit', Number(e.target.value))}
                hint="Max reschedules allowed per booking made against this package"
                data-testid="candeza-package-reschedule-limit-input"
              />
            </Grid>
          </Section>

          <Section id="price">
            <SectionTitle>Price</SectionTitle>
            <Grid>
              <Input
                label="Price (₹)"
                type="number"
                min={0}
                step="0.01"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                data-testid="candeza-package-price-input"
              />
            </Grid>
            <ToggleRow>
              <input
                type="checkbox"
                checked={form.tax_inclusive}
                onChange={(e) => update('tax_inclusive', e.target.checked)}
                data-testid="candeza-package-tax-inclusive-toggle"
              />
              Price is tax-inclusive
            </ToggleRow>
          </Section>

          <Section id="validity">
            <SectionTitle>Validity</SectionTitle>
            <Grid>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Validity type</label>
                <select
                  value={form.validity_type}
                  onChange={(e) => update('validity_type', e.target.value as ValidityType)}
                  data-testid="candeza-package-validity-type-select"
                  style={{ width: '100%', height: 44, borderRadius: 8, border: '1px solid #e2e8f0', padding: '0 12px' }}
                >
                  <option value="by_range">By range (N days)</option>
                  <option value="by_date">By fixed date</option>
                </select>
              </div>
              {form.validity_type === 'by_range' ? (
                <Input
                  label="Validity (days)"
                  type="number"
                  min={rules.package_validity_days_min}
                  max={rules.package_validity_days_max}
                  value={String(form.validity_days)}
                  onChange={(e) => update('validity_days', Number(e.target.value))}
                  data-testid="candeza-package-validity-days-input"
                />
              ) : (
                <Input
                  label="Valid until"
                  type="date"
                  value={validityEndDate}
                  onChange={(e) => setValidityEndDate(e.target.value)}
                  data-testid="candeza-package-validity-end-date-input"
                />
              )}
            </Grid>
          </Section>

          <Section id="enrollment">
            <SectionTitle>Enrollment limit</SectionTitle>
            <Grid>
              <Input
                label="Days per week (optional)"
                type="number"
                min={1}
                max={7}
                value={form.enrollment_limit_days_per_week ? String(form.enrollment_limit_days_per_week) : ''}
                onChange={(e) =>
                  update(
                    'enrollment_limit_days_per_week',
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                data-testid="candeza-package-enrollment-limit-input"
              />
            </Grid>
            {form.enrollment_limit_days_per_week && (
              <p style={{ fontSize: 12, color: '#64748b' }} data-testid="candeza-package-enrollment-limit-preview">
                Students may attend up to {form.enrollment_limit_days_per_week} day(s)/week on this package.
              </p>
            )}
          </Section>

          <Section id="includes">
            <SectionTitle>Includes</SectionTitle>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Subjects</div>
            <div
              data-testid="candeza-package-subjects-list"
              style={{
                display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 160,
                overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8,
                marginBottom: 16,
              }}
            >
              {subjects.length === 0 && (
                <span style={{ color: '#94a3b8', fontSize: 13 }}>No active subjects yet — create one first.</span>
              )}
              {subjects.map((s) => (
                <SubjectChip
                  key={s.id}
                  $active={form.subject_ids.includes(s.id)}
                  data-testid={`candeza-package-subject-option-${s.id}`}
                >
                  <input
                    type="checkbox"
                    checked={form.subject_ids.includes(s.id)}
                    onChange={() => toggleSubject(s.id)}
                  />
                  {s.name}
                </SubjectChip>
              ))}
            </div>
            {form.subject_ids.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {selectedSubjectNames.map((n) => <Badge key={n} variant="primary" size="sm">{n}</Badge>)}
              </div>
            )}

            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Registration fee <HelpTip tipKey="packages.registration_fee" />
            </div>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={regFeeInput}
              onChange={(e) => setRegFeeInput(e.target.value)}
              placeholder="₹0.00"
              data-testid="candeza-package-registration-fee-input"
              style={{ maxWidth: 200, marginBottom: 16 }}
            />

            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add-ons</div>
            {addOnRows.map((a, idx) => (
              <AddOnRow key={idx} data-testid={`candeza-package-addon-row-${idx}`}>
                <Input
                  value={a.name}
                  placeholder="Add-on name"
                  onChange={(e) => setAddOn(idx, { name: e.target.value })}
                  data-testid={`candeza-package-addon-name-${idx}`}
                />
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={paiseToRupeeInput(a.price)}
                  onChange={(e) => setAddOn(idx, { price: rupeeInputToPaise(e.target.value) })}
                  placeholder="₹"
                  data-testid={`candeza-package-addon-price-${idx}`}
                />
                <Button variant="ghost" size="sm" onClick={() => removeAddOnRow(idx)}>Remove</Button>
              </AddOnRow>
            ))}
            <Button variant="outline" size="sm" onClick={addAddOnRow} data-testid="candeza-package-addon-add-btn">
              + Add-on
            </Button>

            {addOnRows.length > 0 && (
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                Total incl. add-ons: {formatPaise(
                  rupeeInputToPaise(priceInput) + addOnRows.reduce((sum, a) => sum + a.price, 0),
                )}
              </p>
            )}
          </Section>
        </Card>
      </Layout>
    </PageContainer>
  );
};


